/**
 * CrearClienteModal — Modal para crear cliente nuevo
 *
 * Se abre con el evento global "crew-action" → "new-cliente".
 * Form mínimo + activate flow opcional (dispara onboarding completo).
 */
import { useState, useEffect } from "react";
import {
  X, Plus, Building2, Mail, Phone, MapPin, Hash, Sparkles,
  Loader2, AlertCircle, CheckCircle2,
} from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";
import { Button } from "./ui/button.jsx";
import { Input } from "./ui/input.jsx";
import { useToast } from "./Toast.jsx";

const CATEGORIAS = [
  { value: "Categoria A: Cliente 360",              label: "A · Cliente 360" },
  { value: "Categoría B: Mkt Digital Full Pack",     label: "B · Mkt Digital Full Pack" },
  { value: "Categoría C: Mkt Digital Starter Pack",  label: "C · Mkt Digital Starter Pack" },
  { value: "Categoría D: Eventos/Eventos Deportivos",label: "D · Eventos" },
  { value: "Categoría E: Pack de Diseño",            label: "E · Pack de Diseño" },
  { value: "Categoría F: Pack de Mkt",               label: "F · Pack de Mkt" },
  { value: "Categoría G: Producciones Audiovisuales", label: "G · Producciones Audiovisuales" },
];

export function CrearClienteModal() {
  const { toast } = useToast();
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [form,   setForm]   = useState(emptyForm());

  function emptyForm() {
    return {
      nombre:           "",
      categoria:        "",
      industria:        "",
      email:            "",
      telefono:         "",
      direccion:        "",
      contacto_principal:"",
      fee_mensual:      "",
      moneda:           "USD",
      plan_tipo:        "retainer",
      estado:           "activo",
      es_sensible:      "FALSE",
      activarOnboarding: true,  // por defecto, crea Drive + carpetas + banco
    };
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.detail === "new-cliente") {
        setForm(emptyForm());
        setError(null);
        setOpen(true);
      }
    };
    window.addEventListener("crew-action", handler);
    return () => window.removeEventListener("crew-action", handler);
  }, []);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function handleClose() { if (!saving) { setOpen(false); setError(null); } }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!form.nombre.trim()) { setError("El nombre es obligatorio"); return; }

    setSaving(true);
    setError(null);
    const toastId = toast.loading("Creando cliente…");

    try {
      // 1. Crear cliente en CLIENTES
      const { activarOnboarding, ...clienteData } = form;
      const createRes = await api.createCliente(clienteData);
      const clienteId = createRes.id;

      // 2. Si pidió activar onboarding, disparar flow completo
      if (activarOnboarding && clienteId) {
        toast.update(toastId, "loading", "Activando onboarding…", {
          description: "Creando Drive + carpetas + Banco de Recursos",
        });
        await api.triggerClienteActivado(clienteId);
      }

      toast.update(toastId, "success", "Cliente creado", {
        description: clienteId,
      });
      window.dispatchEvent(new CustomEvent("crew-refresh", { detail: "clientes" }));
      setOpen(false);
    } catch (e) {
      setError(e.message);
      toast.update(toastId, "error", "Error al crear", { description: e.message });
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative z-10 w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white border border-border rounded-2xl shadow-2xl shadow-[#CDD0D7]/50">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D1FAE5] ring-1 ring-[#6EE7B7] flex items-center justify-center">
              <Building2 className="h-4 w-4 text-[#065F46]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Nuevo Cliente</h2>
              <p className="text-[12px] text-muted-foreground">Crear cliente y opcionalmente activar onboarding completo</p>
            </div>
          </div>
          <button onClick={handleClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground disabled:opacity-30">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <FormField label="Nombre del cliente" required icon={Building2}>
            <Input
              value={form.nombre}
              onChange={e => setField("nombre", e.target.value)}
              placeholder="La Consolidada"
              autoFocus
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Categoría" icon={Hash}>
              <select value={form.categoria} onChange={e => setField("categoria", e.target.value)} className={selectCls}>
                <option value="">Sin categoría</option>
                {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </FormField>
            <FormField label="Industria" icon={Hash}>
              <Input
                value={form.industria}
                onChange={e => setField("industria", e.target.value)}
                placeholder="Seguros, F&B, Educación..."
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Contacto principal" icon={Building2}>
              <Input
                value={form.contacto_principal}
                onChange={e => setField("contacto_principal", e.target.value)}
                placeholder="Nombre del contacto"
              />
            </FormField>
            <FormField label="Email" icon={Mail}>
              <Input
                type="email"
                value={form.email}
                onChange={e => setField("email", e.target.value)}
                placeholder="marketing@cliente.com"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Teléfono / WhatsApp" icon={Phone}>
              <Input
                value={form.telefono}
                onChange={e => setField("telefono", e.target.value)}
                placeholder="595..."
              />
            </FormField>
            <FormField label="Dirección" icon={MapPin}>
              <Input
                value={form.direccion}
                onChange={e => setField("direccion", e.target.value)}
                placeholder="Ciudad, país"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Fee mensual" icon={Hash}>
              <Input
                type="number"
                value={form.fee_mensual}
                onChange={e => setField("fee_mensual", e.target.value)}
                placeholder="0"
              />
            </FormField>
            <FormField label="Moneda" icon={Hash}>
              <select value={form.moneda} onChange={e => setField("moneda", e.target.value)} className={selectCls}>
                <option value="USD">USD</option>
                <option value="PYG">PYG</option>
              </select>
            </FormField>
            <FormField label="Tipo de plan" icon={Hash}>
              <select value={form.plan_tipo} onChange={e => setField("plan_tipo", e.target.value)} className={selectCls}>
                <option value="retainer">Retainer</option>
                <option value="proyecto">Proyecto</option>
                <option value="mixto">Mixto</option>
              </select>
            </FormField>
          </div>

          {/* Activar onboarding toggle */}
          <div className="bg-[#E0F7FB] border border-[#A5F3FC] rounded-lg p-3 flex items-start gap-3">
            <input
              id="activar-onb"
              type="checkbox"
              checked={form.activarOnboarding}
              onChange={e => setField("activarOnboarding", e.target.checked)}
              className="mt-0.5"
            />
            <label htmlFor="activar-onb" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#155E75]" />
                <span className="text-xs font-semibold text-foreground">Activar onboarding automático</span>
              </div>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Crea estructura Drive completa, inicializa Banco de Recursos (34 items), notifica al COM/COO/EC.
              </p>
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] text-xs">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-border/40">
            <Button variant="ghost" type="button" onClick={handleClose} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="ml-auto gap-1.5">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {saving ? "Creando…" : "Crear cliente"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, icon: Icon, children, required }) {
  return (
    <div>
      <label className="flex items-center gap-1 text-[12px] font-medium text-muted-foreground/70 mb-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {required && <span className="text-[#DC2626] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const selectCls = "w-full h-9 px-3 rounded-lg bg-white border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors";
